<template>
  <div>
  </div>
</template>

<script>
import Swal from "sweetalert2";

import { mapActions, mapGetters } from "vuex";

const TOTAL_SESSION_DURATION_MINUTES = process.env.VUE_APP_SESSION_TIMEOUT_MINUTES || 20; // Default to 20 minutes if undefined 

export default {
  name: "SessionTimeOutModalManager",
  data() {
    const modalCountdownDurationMinutes = TOTAL_SESSION_DURATION_MINUTES / 2;
    const initialModalCountdownMs = modalCountdownDurationMinutes * 60 * 1000;

    return {
      sesssionPopupDisplayed: false,
      countdownTime: initialModalCountdownMs,
      initialCountdownMs: initialModalCountdownMs,
      timerRef: 0,
      idleConfirmTimeout: null,
      sessionLogoutMessageRef: null,
      showCancelButtonStatus: true,
      sessionLogoutWarning: `Your session is about to expire in <b></b>. AGATRS was idle for ${modalCountdownDurationMinutes} minutes.`,
      sessionEndedMessage: `Your session has expired after ${TOTAL_SESSION_DURATION_MINUTES} minutes of inactivity.`,
      modalDisplayTriggerMinutes: modalCountdownDurationMinutes,
      backgroundRefreshInterval: null,
      refreshInFlight: false,
      countdownStartTime: null, // Track when countdown actually started for consistent timing
    };
  },
  computed: {
    ...mapGetters({
      isLoggedIn: "authentication/isLoggedIn",
    }),
    isIdle() {
  // Use IdleVue-provided computed per docs
  return this.isAppIdle;
    },
    formatCountdownTimer() {
      if (this.countdownTime > 0) {
        return new Date(this.countdownTime).toISOString().slice(14, -5);
      }
      return "00:00";
    },
  },
  watch: {
    isLoggedIn: {
      handler(newVal, oldVal) {
        if (newVal && !oldVal) {
          // User just logged in - reset IdleVue and initialize session tracking
          this.initializeSessionForLoggedInUser();
        } else if (!newVal && oldVal) {
          // User logged out - clear any session timers
          this.clearCountdownTimer();
          this.stopBackgroundSessionRefresh();
          this.sesssionPopupDisplayed = false;
        }
      },
      immediate: true, // Check immediately on component creation
    },
    isIdle(value, oldValue) {
      if (value) {
        // Add a small grace delay to avoid transient idle flips causing sporadic popups
        // Note: IdleVue continues tracking even when tab is hidden (keepTracking: true)
        if (this.idleConfirmTimeout) {
          clearTimeout(this.idleConfirmTimeout);
          this.idleConfirmTimeout = null;
        }
        this.idleConfirmTimeout = setTimeout(() => {
          this.idleConfirmTimeout = null;
          if (this.isLoggedIn && this.isIdle && !this.sesssionPopupDisplayed && this.countdownTime >= 1000) {
            this.sesssionPopupDisplayed = true;
            this.showSessionWarning();
          }
        }, 750); // 750ms grace period before showing popup
      } else {
        // Clear any pending idle confirmation on activity
        if (this.idleConfirmTimeout) {
          clearTimeout(this.idleConfirmTimeout);
          this.idleConfirmTimeout = null;
        }
        // User became active: if no popup is displayed, clear any countdown timers
        if (!this.sesssionPopupDisplayed) {
          this.clearCountdownTimer();
        }
      }
    },
  },
  methods: {
    ...mapActions({
      restoreContext: "authentication/restoreContext",
      refreshSession: "authentication/refreshSession",
      clearAllLogoutData: "authentication/clearAllLogoutData",
    }),
    decrementCountdownTimer() {
      // Calculate actual remaining time based on start time for consistency
      // Countdown continues accurately even if tab is hidden
      if (this.countdownStartTime) {
        const elapsed = Date.now() - this.countdownStartTime;
        const remaining = Math.max(0, this.initialCountdownMs - elapsed);
        this.countdownTime = remaining;
      } else {
        // Fallback to simple decrement if start time not set
        this.countdownTime = Math.max(0, this.countdownTime - 1000);
      }

      if (this.countdownTime > 1000) {
        // Update the modal display if it's visible
        const swalHtmlContainer = document.querySelector('.swal2-html-container'); 
        if (swalHtmlContainer) {
            const boldTag = swalHtmlContainer.querySelector("b");
            if (boldTag) {
                boldTag.innerHTML = this.formatCountdownTimer;
            }
        }
      } else {
        // Time expired - automatically log out user
        this.handleSessionTimeout();
      }
    },

    handleSessionTimeout() {
      // Update modal display to show session ended message
      const swalHtmlContainer = document.querySelector('.swal2-html-container');
      if (swalHtmlContainer) {
        swalHtmlContainer.innerHTML = this.sessionEndedMessage;
      }

      const confirmButton = document.querySelector(".swal2-confirm");
      const cancelButton = document.querySelector(".swal2-cancel");

      if (confirmButton) {
        confirmButton.style.display = "none";
      }
      if (cancelButton) {
        cancelButton.innerHTML = "Return To Login";
        cancelButton.style.display = "inline-block";
      }
      
      // Clear the countdown timer
      clearInterval(this.timerRef);
      this.timerRef = 0;
      this.countdownStartTime = null;

      // Automatically log out the user - consistent behavior
      // regardless of tab visibility
      if (this.isLoggedIn) {
          this.clearAllLogoutData();
      }
    },
    clearCountdownTimer() {
      clearInterval(this.timerRef);
      this.timerRef = 0;
      this.sesssionPopupDisplayed = false;
      this.countdownTime = this.initialCountdownMs;
      this.countdownStartTime = null; // Reset start time for consistent timing
      // console.log('clearCountdownTimer: sesssionPopupDisplayed reset to false, countdownTime reset to', this.countdownTime);
    },
    showSessionWarning() {
      if (typeof Swal.isVisible === 'function' && Swal.isVisible()) {
        Swal.close();
      }
      
      try {
  // Pause background refresh while the modal is displayed
  this.stopBackgroundSessionRefresh();
        this.sessionLogoutMessageRef = Swal.fire({ 
          title: "Session Expiration Warning",
          html: `${this.sessionLogoutWarning.replace('<b></b>', `<b>${this.formatCountdownTimer}</b>`)}`,
          icon: "warning", 
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: this.showCancelButtonStatus,
          confirmButtonColor: "#10b45a",
          cancelButtonColor: "red",
          cancelButtonText: "Logout",
          confirmButtonText: "Continue",
          reverseButtons: false,
          didOpen: (_modalElement) => { 
            if (this.timerRef === 0) {
              this.countdownStartTime = new Date(); // Record when countdown starts
              this.timerRef = setInterval(() => {
                this.decrementCountdownTimer();
              }, 1000);
            } else {
              // console.log('Interval already running or not reset. timerRef:', this.timerRef);
            }
          }
        }).then(async (result) => {
          this.clearCountdownTimer(); 
          let shouldRestartRefresh = true;

          if (result.dismiss && (result.dismiss === Swal.DismissReason.cancel || result.dismiss === 'cancel')) {
            // console.log('User dismissed/logged out via modal button.');
            if (this.isLoggedIn) {
              this.clearAllLogoutData();
            }
            // Do not restart background refresh if user chose to logout
            shouldRestartRefresh = false;
          } else if (result.isConfirmed) { 
            // console.log('User clicked Continue. Restoring context and refreshing session cookies.');
            try {
              // First refresh the session cookies
              const refreshSuccess = await this.refreshSession();
              if (refreshSuccess) {
                // Then restore context to ensure we have latest data
                await this.restoreContext();
              } else {
                // If refresh failed, try regular context restoration
                // console.warn('Session refresh failed, attempting context restoration');
                await this.restoreContext();
              }
            } catch (error) {
              // console.error("Failed to refresh session and restore context:", error);
              if (this.isLoggedIn) {
                this.clearAllLogoutData();
              }
              // Error icon needs to be different
              Swal.fire({
                  title: "Session Renewal Failed",
                  text: "Could not renew your session. Please log in again.",
                  icon: "error" // Distinct icon
              });
              // If renewal failed and we logged out, avoid restarting refresh
              shouldRestartRefresh = false;
            }
          } else if (result.isDismissed) { 
              //console.log('SweetAlert dismissed for other reason. Reason:', result.dismiss);
          }

          // Resume background refresh if user stayed logged in and we didn't explicitly avoid it
          if (shouldRestartRefresh && this.isLoggedIn) {
            this.startBackgroundSessionRefresh();
          }
        }).catch(err => {
            // console.error("Error with SweetAlert promise (Swal.fire):", err);
            this.clearCountdownTimer(); 
            // Resume background refresh if still logged in
            if (this.isLoggedIn) {
              this.startBackgroundSessionRefresh();
            }
        });
      } catch (e) {
        // console.error("Error directly invoking 'Swal.fire()':", e); 
        this.sesssionPopupDisplayed = false; 
        // Ensure background refresh is running if still logged in
        if (this.isLoggedIn) {
          this.startBackgroundSessionRefresh();
        }
      }
    },

    /**
     * Handle the custom event when user context is restored
     * @param {CustomEvent} event - The context restoration event
     */
    handleContextRestored(event) {
      this.initializeSessionForLoggedInUser();
    },

    /**
     * Initialize session management for a logged-in user
     * Called when user logs in or app starts with existing session
     */
    initializeSessionForLoggedInUser() {
      try {
        // Reset countdown timer to initial value
        this.countdownTime = this.initialCountdownMs;
        
        // Reset IdleVue if available
        if (this.$idle) {
          this.$idle.reset();
        }
        
        // Clear any existing session popup
        this.sesssionPopupDisplayed = false;
        this.clearCountdownTimer();
        
        // Start background session refresh
        this.startBackgroundSessionRefresh();
      } catch (error) {
  // console.warn('Error initializing session for logged-in user:', error);
      }
    },

    /**
     * Start background session refresh to keep cookies alive during active use
     * Continues monitoring even when browser tab is hidden to prevent session timeout
     */
    startBackgroundSessionRefresh() {
      // Clear any existing interval
      this.stopBackgroundSessionRefresh();

      // Determine a safe refresh cadence: about half the session duration (min 1 minute)
      const refreshEveryMinutes = Math.max(1, Math.floor(TOTAL_SESSION_DURATION_MINUTES / 2));
      const intervalMs = refreshEveryMinutes * 60 * 1000;

      this.backgroundRefreshInterval = setInterval(async () => {
        if (this.refreshInFlight) return;
        try {
          // Monitor user activity and refresh session regardless of tab visibility
          // Session timeout countdown continues even when browser tab is hidden
          // and session refresh happens based on actual user activity, not tab visibility
          if (
            this.isLoggedIn &&
            !this.sesssionPopupDisplayed &&
            !this.isIdle
          ) {
            this.refreshInFlight = true;
            await this.refreshSession();
          }
        } catch (error) {
          // console.warn('Background session refresh failed:', error);
        } finally {
          this.refreshInFlight = false;
        }
      }, intervalMs);
    },

    /**
     * Stop background session refresh
     */
    stopBackgroundSessionRefresh() {
      if (this.backgroundRefreshInterval) {
        clearInterval(this.backgroundRefreshInterval);
        this.backgroundRefreshInterval = null;
      }
    },

  },
  mounted() {
    // Initialize session management if user is already logged in when component mounts
    if (this.isLoggedIn) {
      this.initializeSessionForLoggedInUser();
    }

    // Listen for custom user context restoration event
    window.addEventListener('user-context-restored', this.handleContextRestored);
  },
  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener('user-context-restored', this.handleContextRestored);
    
    // Clean up background session refresh
    this.stopBackgroundSessionRefresh();
    
    // Clean up countdown timer
    this.clearCountdownTimer();
  },
};
</script>

<style scoped>
/* Scoped styles for the SessionTimeOutModalManager */
</style>